import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchGrades = createAsyncThunk(
  'grades/fetchGrades',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: '/grade/',
        method: 'GET',
      });
      return response.data;
    } catch (error) {
      console.log(error.response?.data);
      return rejectWithValue('Failed to fetch grades');
    }
  },
);

export const createGrade = createAsyncThunk(
  'grades/createGrade',
  async (gradeData, { rejectWithValue }) => {
    try {
      const payload = {
        rangeFrom: gradeData.rangeFrom,
        rangeTo: gradeData.rangeTo,
        grade: gradeData.grade,
      };
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: '/grade/',
        method: 'POST',
        data: payload,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create grade');
    }
  },
);

export const updateGrade = createAsyncThunk(
  'grades/updateGrade',
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: `/grade/`,
        method: 'PUT',
        data,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update grade');
    }
  },
);

export const deleteGrade = createAsyncThunk(
  'grades/deleteGrade',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: `/grade/${id}`,
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete grade');
    }
  },
);

const initialState = {
  grades: [],
  loading: false,
  error: null,
  success: false,
  selectedGrade: null,
};

const gradeSlice = createSlice({
  name: 'grades',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setSelectedGrade: (state, action) => {
      state.selectedGrade = action.payload;
    },
    clearSelectedGrade: (state) => {
      state.selectedGrade = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch grades
      .addCase(fetchGrades.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGrades.fulfilled, (state, action) => {
        state.loading = false;
        state.grades = action.payload;
        state.error = null;
      })
      .addCase(fetchGrades.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create grade
      .addCase(createGrade.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createGrade.fulfilled, (state, action) => {
        state.loading = false;
        state.grades.push(action.payload);
        state.success = true;
        state.error = null;
      })
      .addCase(createGrade.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Update grade
      .addCase(updateGrade.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateGrade.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.grades.findIndex(
          (grade) => grade.id === action.payload.id,
        );
        if (index !== -1) {
          state.grades[index] = action.payload;
        }
        state.success = true;
        state.error = null;
      })
      .addCase(updateGrade.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Delete grade
      .addCase(deleteGrade.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteGrade.fulfilled, (state, action) => {
        state.loading = false;
        state.grades = state.grades.filter(
          (grade) => grade.id !== action.payload,
        );
        state.success = true;
        state.error = null;
      })
      .addCase(deleteGrade.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  setSelectedGrade,
  clearSelectedGrade,
} = gradeSlice.actions;
export default gradeSlice.reducer;
